/**
 * Compliance Framework Utilities
 *
 * Calculates framework coverage based on scan results.
 * Uses frameworks from scan config (not hardcoded mappings).
 */

import type { LLMScanResult } from "@/types/llm";

/**
 * Framework name normalization map.
 * Maps promptfoo framework identifiers to display names.
 */
const FRAMEWORK_DISPLAY_NAMES: Record<string, string> = {
  "eu:ai-act": "EU AI Act",
  "eu-ai-act": "EU AI Act",
  gdpr: "GDPR",
  ccpa: "CCPA",
  "soc-2": "SOC 2",
  soc2: "SOC 2",
  hipaa: "HIPAA",
  "owasp-llm-top-10": "OWASP LLM Top 10",
  "owasp llm top 10": "OWASP LLM Top 10",
};

/**
 * Normalize framework name to display format.
 */
function normalizeFrameworkName(framework: string): string {
  const normalized = framework.toLowerCase().replace(/[:_]/g, "-");
  return FRAMEWORK_DISPLAY_NAMES[normalized] || framework.toUpperCase();
}

/**
 * Framework coverage calculation result.
 */
export interface FrameworkCoverage {
  name: string;
  tested: number;
  passed: number;
  failed: number;
  coverage: number; // Percentage (0-100)
}

/**
 * Calculate framework coverage based on scan results.
 *
 * Since promptfoo runs all tests under the configured frameworks,
 * all test results count toward all configured frameworks.
 *
 * @param frameworks - List of frameworks configured for the scan (e.g., ["eu:ai-act"])
 * @param results - All scan results
 * @returns Array of framework coverage metrics
 */
export function calculateFrameworkCoverage(
  frameworks: string[],
  results: LLMScanResult[]
): FrameworkCoverage[] {
  if (frameworks.length === 0 || results.length === 0) {
    return [];
  }

  // Normalize framework names to display format
  const normalizedFrameworks = frameworks.map(normalizeFrameworkName);

  // Calculate overall stats (all tests count toward all frameworks)
  const totalTests = results.length;
  const passedTests = results.filter((r) => r.status === "PASS").length;
  const failedTests = results.filter((r) => r.status === "FAIL").length;
  const coverage = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

  // Return coverage for each framework (same stats for all since all tests apply to all frameworks)
  return normalizedFrameworks.map((fw) => ({
    name: fw,
    tested: totalTests,
    passed: passedTests,
    failed: failedTests,
    coverage: Math.round(coverage * 10) / 10, // Round to 1 decimal place
  }));
}
